/** "Wait for" steps (ADR-0091): an event from another system, a time, a person's answer or a
 * return from a page, whichever comes first; and how the console says a run is waiting. */
export default {
  en: {
    what: "What ends the wait (whichever comes first)",
    whatHelp: "The run pauses here without using anything. It can wait for days.",
    by: {
      event: "Another system tells us",
      time: "A time passes or a date comes",
      answer: "A person answers",
    },
    event: {
      name: "Event name",
      nameHelp: "The name the other system sends, e.g. payment.received.",
      key: "Which event belongs to this run",
      keyHelp: "The value both sides know, e.g. the order number. The event's ‘key’ must be the same.",
      keyPick: "Pick a value from earlier steps",
      address: "Where the other system sends it",
      addressHelp: "POST to this address, signed like this agent's webhook (Triggers): header X-AP-Signature = sha256=HMAC of the body with the agent's webhook secret. Body: {\"event\": name, \"key\": value, \"id\": unique id, \"data\": {…}}. An event sent a little before the run waits still counts (kept a week).",
    },
    time: {
      mode: "When",
      after: "After a while",
      at: "At a date and time from earlier steps",
      afterLabel: "Go on after",
      atLabel: "Go on at",
      atHelp: "A date and time with its time zone, e.g. a delivery slot.",
      working: "Count working hours only",
      workingHelp: "Uses the organisation's business calendar (working days, hours and holidays).",
    },
    answer: {
      question: "The question",
      questionAr: "Question (Arabic)",
      questionEn: "Question (English)",
      who: "Who may answer",
      whoHelp: "Empty: the person who started the run answers. With roles, only people with them answer; the person who started the run never sees the question.",
      requesterToo: "The person who started the run may answer too",
    },
    giveUp: "If nothing comes in time",
    giveUpAfter: "Stop waiting after",
    giveUpHelp: "Nothing ends the wait by then: the run goes on along the path you choose.",
    giveUpTo: "Then go to",
    giveUpOn: "Go on as usual",
    nothing: "Choose at least one thing to wait for, or a ‘continue elsewhere’ button that comes back here.",
    returns: "A ‘continue elsewhere’ button comes back here: the wait also ends when the person returns from that page.",
    makes: "What ended the wait, when, and the event's values or the person's answer",
    dur: {
      PT15M: "15 minutes",
      PT30M: "30 minutes",
      PT1H: "1 hour",
      PT2H: "2 hours",
      PT4H: "4 hours",
      PT8H: "8 hours",
      P1D: "1 day",
      P2D: "2 days",
      P3D: "3 days",
      P5D: "5 days",
      P7D: "1 week",
      P14D: "2 weeks",
      P30D: "30 days",
      P60D: "60 days",
      P90D: "90 days",
    },
    action: {
      event: "Waits for ‘{{event}}’",
      time: "Waits {{time}}",
      at: "Waits until a set time",
      answer: "Waits for a person's answer",
      return: "Waits for the person to come back",
      nothing: "Waits (nothing chosen yet)",
    },
    status: {
      waiting: "Waiting",
      for: {
        event: "for ‘{{event}}’ from another system",
        answer: "for a person's answer",
        return: "for the person to come back from another page",
        time: "until the set time",
      },
      givesUp: "Gives up {{when}}",
      until: "Goes on {{when}}",
    },
    test: {
      title: "Try the wait without the other system",
      event: "Send a test event",
      return: "Come back from the page",
      time: "Skip the wait",
      sent: "Sent. The run goes on.",
      data: "Event values (JSON)",
    },
    story: {
      waiting: "Waited at ‘{{step}}’",
      ended: {
        event: "The event came: ‘{{event}}’.",
        answer: "A person answered.",
        return: "The person came back from the page.",
        time: "The time came.",
        gave_up: "Nothing came in time; it stopped waiting.",
        stopped: "The run was stopped while waiting.",
      },
    },
  },
  ar: {
    what: "ما الذي ينهي الانتظار (أيّها يأتي أولاً)",
    whatHelp: "يتوقف التشغيل هنا دون أن يستهلك شيئاً، ويمكنه الانتظار أياماً.",
    by: {
      event: "يخبرنا نظام آخر",
      time: "يمرّ وقت أو يحين موعد",
      answer: "يجيب شخص",
    },
    event: {
      name: "اسم الحدث",
      nameHelp: "الاسم الذي يرسله النظام الآخر، مثل payment.received.",
      key: "أي حدث يخص هذا التشغيل",
      keyHelp: "قيمة يعرفها الطرفان، مثل رقم الطلب. يجب أن يكون ‘key’ في الحدث هو نفسه.",
      keyPick: "اختر قيمة من الخطوات السابقة",
      address: "أين يرسله النظام الآخر",
      addressHelp: "أرسل طلب POST إلى هذا العنوان موقّعاً مثل خطاف الويب لهذا الوكيل (المشغّلات): الترويسة X-AP-Signature = sha256=HMAC لمحتوى الطلب بسرّ خطاف الويب للوكيل. المحتوى: {\"event\": الاسم، \"key\": القيمة، \"id\": معرّف فريد، \"data\": {…}}. الحدث الذي يصل قبل بدء الانتظار بقليل يُحتسب أيضاً (يُحفظ أسبوعاً).",
    },
    time: {
      mode: "متى",
      after: "بعد مدة",
      at: "في تاريخ ووقت من الخطوات السابقة",
      afterLabel: "المتابعة بعد",
      atLabel: "المتابعة في",
      atHelp: "تاريخ ووقت مع المنطقة الزمنية، مثل موعد توصيل.",
      working: "احتساب ساعات العمل فقط",
      workingHelp: "يستخدم تقويم العمل الخاص بالمؤسسة (أيام العمل وساعاته والعطل).",
    },
    answer: {
      question: "السؤال",
      questionAr: "السؤال (بالعربية)",
      questionEn: "السؤال (بالإنجليزية)",
      who: "من يمكنه الإجابة",
      whoHelp: "إذا تُرك فارغاً يجيب الشخص الذي بدأ التشغيل. مع الأدوار يجيب أصحابها فقط، ولا يرى الشخص الذي بدأ التشغيل السؤال.",
      requesterToo: "يمكن للشخص الذي بدأ التشغيل أن يجيب أيضاً",
    },
    giveUp: "إذا لم يصل شيء في الوقت",
    giveUpAfter: "التوقف عن الانتظار بعد",
    giveUpHelp: "إذا لم ينتهِ الانتظار حتى ذلك الحين، يتابع التشغيل في المسار الذي تختاره.",
    giveUpTo: "ثم الانتقال إلى",
    giveUpOn: "المتابعة كالمعتاد",
    nothing: "اختر شيئاً واحداً على الأقل للانتظار، أو زر ‘متابعة في مكان آخر’ يعود إلى هنا.",
    returns: "يعود زر ‘متابعة في مكان آخر’ إلى هنا: ينتهي الانتظار أيضاً عندما يعود الشخص من تلك الصفحة.",
    makes: "ما أنهى الانتظار ومتى، وقيم الحدث أو إجابة الشخص",
    dur: {
      PT15M: "15 دقيقة",
      PT30M: "30 دقيقة",
      PT1H: "ساعة واحدة",
      PT2H: "ساعتان",
      PT4H: "4 ساعات",
      PT8H: "8 ساعات",
      P1D: "يوم واحد",
      P2D: "يومان",
      P3D: "3 أيام",
      P5D: "5 أيام",
      P7D: "أسبوع",
      P14D: "أسبوعان",
      P30D: "30 يوماً",
      P60D: "60 يوماً",
      P90D: "90 يوماً",
    },
    action: {
      event: "ينتظر ‘{{event}}’",
      time: "ينتظر {{time}}",
      at: "ينتظر حتى موعد محدد",
      answer: "ينتظر إجابة شخص",
      return: "ينتظر عودة الشخص",
      nothing: "ينتظر (لم يُختر شيء بعد)",
    },
    status: {
      waiting: "في الانتظار",
      for: {
        event: "لـ ‘{{event}}’ من نظام آخر",
        answer: "لإجابة شخص",
        return: "لعودة الشخص من صفحة أخرى",
        time: "حتى الموعد المحدد",
      },
      givesUp: "يتوقف عن الانتظار {{when}}",
      until: "يتابع {{when}}",
    },
    test: {
      title: "جرّب الانتظار دون النظام الآخر",
      event: "أرسل حدثاً تجريبياً",
      return: "العودة من الصفحة",
      time: "تخطَّ الانتظار",
      sent: "أُرسل. يتابع التشغيل.",
      data: "قيم الحدث (JSON)",
    },
    story: {
      waiting: "انتظر عند ‘{{step}}’",
      ended: {
        event: "وصل الحدث: ‘{{event}}’.",
        answer: "أجاب شخص.",
        return: "عاد الشخص من الصفحة.",
        time: "حان الموعد.",
        gave_up: "لم يصل شيء في الوقت، فتوقف عن الانتظار.",
        stopped: "أُوقف التشغيل أثناء الانتظار.",
      },
    },
  },
};
